import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Send, AlertCircle, CheckCircle2, Clock, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const fadeUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 },
};

const stages = [
 { value: "idea", label: "Just an idea" },
 { value: "validating", label: "Validating / Pre-revenue" },
 { value: "early-revenue", label: "Early revenue (< $10k MRR)" },
 { value: "growing", label: "Growing ($10k-$50k MRR)" },
 { value: "scaling", label: "Scaling ($50k+ MRR)" },
];

const timelines = [
 { value: "asap", label: "As soon as possible" },
 { value: "1-2-weeks", label: "1-2 weeks" },
 { value: "1-month", label: "Within a month" },
 { value: "exploring", label: "Just exploring options" },
];

const expectations = [
 "We review every submission personally.",
 "You will hear back within 48 hours.",
 "If there is a potential fit, we will schedule a brief call.",
 "If we are not the right fit, we will tell you honestly.",
];

// Path from chaos to structure illustration
const illustration = "/images/contact.png";

const API_URL = process.env.REACT_APP_BACKEND_URL || ''


export default function ContactPage() {
 const [submitted, setSubmitted] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [formData, setFormData] = useState({
  name: "",
  email: "",
  phone: "",
  company: "",
  stage: "",
  timeline: "",
  budget: "",
  description: "",
  challenge: "",
 });

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validate required fields
  if (!formData.name || !formData.email || !formData.description) {
   toast.error("Please fill in all required fields.");
   return;
  }
  
  setSubmitting(true);
  
  try {
   // Submit to backend API
   const response = await fetch(`${API_URL}/api/leads`, {
    method: 'POST',
    headers: {
     'Content-Type': 'application/json'
    },
    body: JSON.stringify({
     name: formData.name,
     email: formData.email,
     phone: formData.phone || null,
     company: formData.company || null,
     stage: formData.stage || null,
     budget: formData.budget || null,
     timeline: formData.timeline || null,
     message: formData.description,
     how_can_help: formData.challenge || null
    })
   });

   if (response.ok) {
    setSubmitted(true);
    toast.success("Wishlist submitted successfully!");
   } else {
    throw new Error('Failed to submit');
   }
  } catch (error) {
   console.error("Submission error:", error);
   toast.error("Something went wrong. Please try again.");
  } finally {
   setSubmitting(false);
  }
 };

 const handleChange = (field, value) => {
  setFormData(prev => ({ ...prev, [field]: value }));
 };

 if (submitted) {
  return (
   <div className="flex flex-col">
    <section className="bg-hero py-20 sm:py-28 lg:py-32">
     <div className="container-narrow">
      <motion.div
       initial={{ opacity: 0, scale: 0.95 }}
       animate={{ opacity: 1, scale: 1 }}
       transition={{ duration: 0.5 }}
       className="text-center"
      >
       <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="h-8 w-8 text-success" />
       </div>
       <h1 className="heading-display text-foreground mb-6">
        Wishlist Received
       </h1>
       <p className="body-large text-muted-foreground max-w-2xl mx-auto mb-8">
        Thank you for sharing what you are working on. We review every submission personally and will get back to you within 48 hours.
       </p>
       <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button variant="outline" asChild className="rounded-full">
         <Link to="/">Back to Home</Link>
        </Button>
        <Button variant="accent" asChild className="rounded-full">
         <Link to="/blog">Read Our Blog</Link>
        </Button>
       </div>
      </motion.div>
     </div>
    </section>
   </div>
  );
 }

 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       Submit Your Wishlist
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       Tell us what you are building. We will tell you honestly if we can help. and how.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Form Section */}
   <section className="bg-background py-16 sm:py-20">
    <div className="container-base">
     <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
      {/* Form */}
      <motion.div
       initial={{ opacity: 0, y: 30 }}
       whileInView={{ opacity: 1, y: 0 }}
       viewport={{ once: true }}
       transition={{ duration: 0.6 }}
       className="lg:col-span-2"
      >
       <Card className="card-elevated">
        <CardHeader>
         <CardTitle className="font-serif text-2xl">Your Wishlist</CardTitle>
         <CardDescription>
          The more context you share, the better we can assess fit.
         </CardDescription>
        </CardHeader>
        <CardContent>
         <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic Info */}
          <div className="grid sm:grid-cols-2 gap-4">
           <div className="space-y-2">
            <Label htmlFor="name">Your Name *</Label>
            <Input
             id="name"
             placeholder="Jane Founder"
             value={formData.name}
             onChange={(e) => handleChange("name", e.target.value)}
             required
             data-testid="contact-name-input"
            />
           </div>
           <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
             id="email"
             type="email"
             placeholder="jane@company.com"
             value={formData.email}
             onChange={(e) => handleChange("email", e.target.value)}
             required
             data-testid="contact-email-input"
            />
           </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
           <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
             id="phone"
             type="tel"
             placeholder="+1 (555) 123-4567"
             value={formData.phone}
             onChange={(e) => handleChange("phone", e.target.value)}
             data-testid="contact-phone-input"
            />
           </div>
           <div className="space-y-2">
            <Label htmlFor="company">Company / Project Name</Label>
            <Input
             id="company"
             placeholder="Acme Inc"
             value={formData.company}
             onChange={(e) => handleChange("company", e.target.value)}
             data-testid="contact-company-input"
            />
           </div>
          </div>

          {/* Stage */}
          <div className="space-y-2">
           <Label>Current Stage</Label>
           <Select value={formData.stage} onValueChange={(value) => handleChange("stage", value)}>
            <SelectTrigger>
             <SelectValue placeholder="Select your current stage" />
            </SelectTrigger>
            <SelectContent>
             {stages.map((stage) => (
              <SelectItem key={stage.value} value={stage.value}>
               {stage.label}
              </SelectItem>
             ))}
            </SelectContent>
           </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
           <Label htmlFor="description">What are you building? *</Label>
           <Textarea
            id="description"
            placeholder="Describe your product or idea. What problem does it solve? Who is it for?"
            className="min-h-[100px]"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            required
           />
          </div>

          {/* Challenge */}
          <div className="space-y-2">
           <Label htmlFor="challenge">What is your biggest challenge right now?</Label>
           <Textarea
            id="challenge"
            placeholder="What is blocking progress? What keeps you up at night?"
            className="min-h-[80px]"
            value={formData.challenge}
            onChange={(e) => handleChange("challenge", e.target.value)}
           />
          </div>

          {/* Timeline */}
          <div className="space-y-3">
           <Label>When do you need to start?</Label>
           <RadioGroup
            value={formData.timeline}
            onValueChange={(value) => handleChange("timeline", value)}
            className="grid sm:grid-cols-2 gap-2"
           >
            {timelines.map((timeline) => (
             <div key={timeline.value} className="flex items-center space-x-2">
              <RadioGroupItem value={timeline.value} id={timeline.value} />
              <Label htmlFor={timeline.value} className="cursor-pointer font-normal">
               {timeline.label}
              </Label>
             </div>
            ))}
           </RadioGroup>
          </div>

          {/* Budget */}
          <div className="space-y-2">
           <Label>Budget Range (optional)</Label>
           <Select value={formData.budget} onValueChange={(value) => handleChange("budget", value)}>
            <SelectTrigger>
             <SelectValue placeholder="Select budget range" />
            </SelectTrigger>
            <SelectContent>
             <SelectItem value="under-10k">Under $10,000</SelectItem>
             <SelectItem value="10-25k">$10,000 - $25,000</SelectItem>
             <SelectItem value="25-50k">$25,000 - $50,000</SelectItem>
             <SelectItem value="50-100k">$50,000 - $100,000</SelectItem>
             <SelectItem value="over-100k">$100,000+</SelectItem>
             <SelectItem value="not-sure">Not sure yet</SelectItem>
            </SelectContent>
           </Select>
          </div>

          <Button 
           type="submit" 
           variant="accent" 
           size="lg" 
           className="w-full rounded-full"
           disabled={submitting}
           data-testid="contact-submit-btn"
          >
           {submitting ? 'Submitting...' : 'Submit Wishlist'}
           {!submitting && <Send className="h-4 w-4 ml-2" />}
          </Button>
         </form>
        </CardContent>
       </Card>
      </motion.div>

      {/* Sidebar */}
      <motion.div
       initial={{ opacity: 0, y: 30 }}
       whileInView={{ opacity: 1, y: 0 }}
       viewport={{ once: true }}
       transition={{ duration: 0.6, delay: 0.1 }}
       className="space-y-6"
      >
       {/* Illustration */}
       <div className="flex justify-center">
        <div className="w-full max-w-[200px]">
         <img
          src={illustration}
          alt="From chaos to clarity"
          className="w-full h-auto"
         />
        </div>
       </div>

       {/* Expectations */}
       <Card className="border-border/50">
        <CardHeader className="pb-3">
         <CardTitle className="font-serif text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-accent" />
          What to Expect
         </CardTitle>
        </CardHeader>
        <CardContent>
         <ul className="space-y-3">
          {expectations.map((item, i) => (
           <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
            {item}
           </li>
          ))}
         </ul>
        </CardContent>
       </Card>

       {/* Note */}
       <div className="bg-muted/30 border border-border/50 rounded-xl p-5">
        <div className="flex items-start gap-3">
         <AlertCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
         <div>
          <h4 className="font-medium text-foreground mb-2">A Note on Fit</h4>
          <p className="text-sm text-muted-foreground">
           We do not work with everyone. We take on a limited number of founders to ensure quality. If we are not the right fit, we will tell you. and try to point you in a better direction.
          </p>
         </div>
        </div>
       </div>

       {/* Alternative */}
       <div className="bg-card border border-border/50 rounded-xl p-5">
        <div className="flex items-start gap-3">
         <MessageSquare className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
         <div>
          <h4 className="font-medium text-foreground mb-2">Prefer to Read First?</h4>
          <p className="text-sm text-muted-foreground mb-4">
           Learn more about how we work before reaching out.
          </p>
          <div className="space-y-2">
           <Button variant="outline" size="sm" asChild className="w-full justify-start rounded-full">
            <Link to="/process">See Our Process</Link>
           </Button>
           <Button variant="outline" size="sm" asChild className="w-full justify-start rounded-full">
            <Link to="/who-we-help">Is This Right for Me?</Link>
           </Button>
          </div>
         </div>
        </div>
       </div>
      </motion.div>
     </div>
    </div>
   </section>
  </div>
 );
}
